import { useMemo } from 'react'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import { HelpCircle } from '@/ui/icons'
import { useTripDetailPanel } from '../contexts/TripDetailPanelContext'
import { Button } from '@/ui/design-system'
import { useMealNeeds } from '@/modules/meals/hooks/useMealNeeds'
import { getMealContributorName } from '@/modules/meals/utils/contributors'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { SectionCard } from './SectionCard'
import { cn } from '@/shared/utils'
import type { MealNeed } from '@/shared/api/mealNeeds'
import { useAuth } from '@/modules/auth'
import { MEAL_PLAN_TOOLTIP_COPY } from '@/shared/copy/tooltips'

interface MealPlanSummarySectionProps {
  tripId: string
  isGuestAccess?: boolean
  trip?: { meals?: unknown[]; start_date?: string | null } | null
  title?: string
  className?: string
}

interface DayGroup {
  dayNumber: number
  dayLabel: string
  meals: MealNeed[]
}

/** Human-readable meal type label for display (no icons). */
function getMealTypeLabel(time: MealNeed['planned_meal_time']): string {
  const labels: Record<string, string> = {
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack'
  }
  return labels[time ?? 'dinner'] ?? 'Dinner'
}

/** Derive day number (1-based) from meal planned_date and trip start; null if unassigned. */
function getDayNumber(meal: MealNeed, tripStartDate: string | null | undefined): number | null {
  if (!meal.planned_date || !tripStartDate) return null
  try {
    const mealDate = parseISO(meal.planned_date)
    const start = parseISO(tripStartDate)
    const dayOfTrip = differenceInCalendarDays(mealDate, start) + 1
    return dayOfTrip >= 1 ? dayOfTrip : null
  } catch {
    return null
  }
}

/** Group sorted meals by trip day. Unassigned (no date) go into a final "Unassigned" group. */
function groupMealsByDay(
  sortedMeals: MealNeed[],
  tripStartDate: string | null | undefined
): DayGroup[] {
  const byDay = new Map<number | 'unassigned', MealNeed[]>()
  for (const meal of sortedMeals) {
    const dayNum = getDayNumber(meal, tripStartDate)
    const key = dayNum ?? 'unassigned'
    if (!byDay.has(key)) byDay.set(key, [])
    byDay.get(key)!.push(meal)
  }
  const groups: DayGroup[] = []
  const dayKeys = [...byDay.keys()].filter((k): k is number => k !== 'unassigned').sort((a, b) => a - b)
  for (const dayNum of dayKeys) {
    groups.push({
      dayNumber: dayNum,
      dayLabel: `Day ${dayNum}`,
      meals: byDay.get(dayNum) ?? []
    })
  }
  const unassigned = byDay.get('unassigned')
  if (unassigned?.length) {
    groups.push({
      dayNumber: 0,
      dayLabel: 'No date',
      meals: unassigned
    })
  }
  return groups
}

export function MealPlanSummarySection({
  tripId,
  isGuestAccess = false,
  trip,
  title = 'Meal Plan',
  className
}: MealPlanSummarySectionProps) {
  const { setSelectedDetail, selectedDetail } = useTripDetailPanel()
  const { user } = useAuth()
  const {
    meals: fetchedMeals,
    isLoading,
    optOutOfMeal,
    optInToMeal,
    isOptingOut
  } = useMealNeeds(tripId, { skip: isGuestAccess })

  const handleAddMeal = () => {
    setSelectedDetail({ type: 'meal-add' })
  }

  const meals = useMemo((): MealNeed[] => {
    if (isGuestAccess && trip?.meals && Array.isArray(trip.meals)) {
      return (trip.meals as MealNeed[]).map((m: MealNeed) => ({
        ...m,
        id: m.id,
        name: (m as { meal_name?: string }).meal_name ?? m.name ?? 'Meal',
        planned_meal_time: (m.planned_meal_time ?? 'dinner') as MealNeed['planned_meal_time']
      }))
    }
    return Array.isArray(fetchedMeals) ? fetchedMeals : []
  }, [isGuestAccess, trip?.meals, fetchedMeals])

  const sortedMeals = useMemo(
    () =>
      [...meals].sort((a, b) => {
        if (!a.planned_date && !b.planned_date) return 0
        if (!a.planned_date) return 1
        if (!b.planned_date) return -1
        const cmp = a.planned_date.localeCompare(b.planned_date)
        if (cmp !== 0) return cmp
        const order = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 }
        return (order[a.planned_meal_time] ?? 4) - (order[b.planned_meal_time] ?? 4)
      }),
    [meals]
  )

  const dayGroups = useMemo(
    () => groupMealsByDay(sortedMeals, trip?.start_date),
    [sortedMeals, trip?.start_date]
  )

  const currentMealId = selectedDetail?.type === 'meal' ? selectedDetail.id : null
  const showOptOut = !isGuestAccess && user

  const sectionTitle = (
    <>
      <span>{title}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="What does the meal plan section do?"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-xs">
          <p className="text-sm text-muted-foreground leading-relaxed">{MEAL_PLAN_TOOLTIP_COPY}</p>
        </TooltipContent>
      </Tooltip>
    </>
  )

  if (isLoading) {
    return (
      <SectionCard title={sectionTitle} count={0} className={className} fixedHeight>
        <div className="animate-pulse h-24 rounded-lg bg-earth-100 dark:bg-earth-800" />
      </SectionCard>
    )
  }

  const addMealButton = !isGuestAccess && (
    <Button variant="outline" size="sm" onClick={handleAddMeal} className="gap-2">
      + Add
    </Button>
  )

  return (
    <SectionCard
      title={sectionTitle}
      count={meals.length}
      className={cn('min-w-0', className)}
      actions={addMealButton}
      contentClassName="pt-2 min-w-0"
      fixedHeight
    >
      {!meals.length ? (
        <div className="space-y-2">
          <p className="text-sm text-earth-500 dark:text-earth-400 py-1.5">No meals planned yet.</p>
        </div>
      ) : (
        <div className="space-y-4 min-w-0">
          {dayGroups.map((group) => (
            <div
              key={group.dayNumber === 0 ? 'unassigned' : group.dayNumber}
              className="flex gap-3 min-w-0"
            >
              <div
                className={cn(
                  'flex-shrink-0 w-20 flex items-center',
                  group.dayNumber === 0 ? 'text-earth-500 dark:text-earth-400' : ''
                )}
              >
                <span className="text-base font-semibold text-earth-900 dark:text-foreground">
                  {group.dayNumber === 0 ? group.dayLabel : `Day ${group.dayNumber}`}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 flex-1 min-w-0">
                {group.meals.map((meal) => {
                  const isSelected = currentMealId === meal.id
                  const mealTypeLabel = getMealTypeLabel(meal.planned_meal_time)
                  const countLabel =
                    meal.total_members != null
                      ? `${meal.eating_count ?? Math.max(0, meal.total_members - (meal.opt_out_count ?? 0))}/${meal.total_members}`
                      : '—'
                  return (
                    <div
                      key={meal.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedDetail({ type: 'meal', id: meal.id })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedDetail({ type: 'meal', id: meal.id })
                        }
                      }}
                      className={cn(
                        'rounded-md border p-3 text-left transition-colors flex flex-col gap-1.5 cursor-pointer overflow-hidden min-w-[160px] max-w-[240px]',
                        isSelected
                          ? 'border-adventure-500 bg-adventure-50 text-adventure-900 dark:border-adventure-500 dark:bg-adventure-900/30 dark:text-adventure-200'
                          : 'border-earth-200 bg-background text-foreground hover:bg-earth-100 dark:border-earth-700 dark:bg-earth-200 dark:hover:bg-earth-900/30 dark:text-earth-200'
                      )}
                    >
                      <span className="text-xs font-medium text-earth-500 dark:text-earth-400">
                        {mealTypeLabel}
                      </span>
                      <div
                        className={cn(
                          'font-semibold text-sm truncate',
                          isSelected
                            ? 'text-adventure-900 dark:text-adventure-200'
                            : 'text-earth-900 dark:text-foreground'
                        )}
                      >
                        {meal.name}
                      </div>
                      <div className="text-xs text-earth-500 dark:text-earth-400 truncate">
                        {getMealContributorName(meal)}
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2 mt-1 pt-1.5 border-t border-earth-200/50 dark:border-earth-700/50">
                        <span
                          className="text-xs text-earth-500 dark:text-earth-400"
                          aria-label={`${countLabel} eating`}
                        >
                          {countLabel}
                        </span>
                        {showOptOut && (
                          <button
                            type="button"
                            className={cn(
                              'rounded px-2 py-0.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-1',
                              meal.current_user_opted_out
                                ? 'bg-earth-200 text-earth-700 dark:bg-earth-700 dark:text-earth-300'
                                : 'bg-success/20 text-success dark:bg-success/30 dark:text-success'
                            )}
                            disabled={isOptingOut}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (meal.current_user_opted_out) {
                                optInToMeal(meal.id)
                              } else {
                                optOutOfMeal(meal.id)
                              }
                            }}
                            aria-label={meal.current_user_opted_out ? 'Opt in' : 'Opt out'}
                          >
                            {meal.current_user_opted_out ? 'Opt in' : 'Opt out'}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  )
}
